import { clamp, Quat, Vec3, vecToBearing, wrapAngle } from '../core/math';
import { deckY, halfBreadthXY, keelY, sheerY, DECK_X_MAX, DECK_X_MIN, deckHalfWidth } from '../data/ship';
import { SHIP_CLASSES, type ShipClass } from '../data/classes';
import { makeWaveSample, type WaveField, type WaveSample } from './waves';
import { Wind } from './wind';
import { SailRig } from './sails';
import type { SimEvents } from './events';

// Rigid-body ship: 6-DOF dynamics with buoyancy integrated over a column
// discretisation of the real hull lines, hydrodynamic drag relative to the
// orbital velocity of the waves, keel lift, a rudder and the sail forces.
//
// Every class shares the design hull (data/ship.ts) scaled per axis. The
// physics works in physical ship-local metres; rig, deck and damage data stay
// in design space and are converted with designToLocal().

const RHO = 1025;
const G = 9.81;
const AIR = 1.225;

interface Column {
  x: number;
  z: number;
  /** Local y of each volume element's centre, and its height. */
  ys: Float64Array;
  dys: Float64Array;
  /** Plan area of the column. */
  area: number;
  /** Lateral area share (1 / number of columns across the beam at this station). */
  latShare: number;
  /** Warm start for the wave inversion. */
  x0: number;
  z0: number;
  hasWarm: boolean;
  water: WaveSample;
  /** Last wave-slam time (for rate limiting events). */
  lastSlam: number;
  bow: boolean;
}

interface RailPoint {
  x: number;
  y: number;
  z: number;
  side: number;
  last: number;
  water: WaveSample;
  /** Where on the undisturbed sea to look next step (warm start), once known. */
  x0: number;
  z0: number;
  warm: boolean;
}

/** A watertight(ish) section of the hold between bulkheads. */
export interface Compartment {
  /** Design-space extent along the keel. */
  x0: number;
  x1: number;
  /** Volume below the deck, m^3 (physical). */
  capacity: number;
  /** Water aboard, m^3. */
  water: number;
  /** Physical local centroid of the space (x, bottom y, z = 0). */
  cx: number;
  bottomY: number;
  topY: number;
  /** Mean half-beam (physical) for the free-surface shift. */
  halfBeam: number;
}

export interface ShipControls {
  /** Helm: -1 hard to port .. +1 hard to starboard. */
  helm: number;
}

export class Ship {
  readonly cls: ShipClass;
  /** Design -> physical scale. */
  readonly sx: number;
  readonly sy: number;
  readonly sz: number;

  // --- state (world)
  /** Centre of mass position / velocity. */
  readonly com = new Vec3();
  readonly vel = new Vec3();
  readonly rot = new Quat();
  /** Angular velocity, world frame. */
  readonly angVel = new Vec3();
  /** Previous pose for render interpolation. */
  readonly prevCom = new Vec3();
  readonly prevRot = new Quat();

  // --- mass properties
  readonly mass: number;
  /** Centre of mass in ship-local coordinates (well ballasted, low). */
  readonly cg = new Vec3(0.4, -1.1, 0);
  /** Principal inertia (local x roll, y yaw, z pitch), including added mass. */
  readonly inertia = new Vec3();
  /** Displaced volume at the design waterline, m^3. */
  readonly displacement: number;

  readonly sails: SailRig;
  readonly columns: Column[] = [];
  readonly compartments: Compartment[] = [];
  private readonly rail: RailPoint[] = [];
  private readonly colDx: number;

  // --- controls & condition
  rudder = 0;
  helm = 0;
  /** Rudder effectiveness 0..1 (shot away tiller ropes, damaged blade). */
  rudderHealth = 1;
  /** Rudder angle it jams at when destroyed. */
  rudderJam = 0;
  /** Crew at the wheel, 0..1: how quickly helm orders take effect. */
  helmCrew = 1;
  /** Extra hull drag from shot holes, trailing wreckage, etc. */
  dragFactor = 1;
  /** Pumps working, m^3/s. */
  pumpRate = 0;
  /** Set when she has gone under. */
  sunk = false;
  /** Seconds the whole rail has been under water. */
  private underTime = 0;

  // --- derived telemetry
  /** Speed through the water along the keel, m/s. */
  speed = 0;
  /** Speed over ground, m/s. */
  sog = 0;
  heading = 0;
  heel = 0;
  pitch = 0;
  leeway = 0;
  /** Apparent wind (ship-local) at masthead height. */
  readonly apparentWind = new Vec3();
  /** Linear acceleration of the hull (ship-local, excl. gravity). */
  readonly accelLocal = new Vec3();
  readonly angVelLocal = new Vec3();
  /** Fraction of the hull volume currently submerged vs design. */
  immersion = 1;
  /** Water on deck, 0..1, decays as it drains through the scuppers. */
  deckWater = 0;
  /** Most recent side of green water (+1 starboard / -1 port). */
  deckWaterSide = 0;
  /** Bow spray intensity 0..1 (speed and pitching into the sea). */
  bowSpray = 0;
  /** Rate of change of heel, for creak audio. */
  strain = 0;
  /** Water in the hold as a fraction of reserve buoyancy. */
  flooding = 0;
  /** Ship-local force totals for debugging / tests. */
  readonly sailForce = new Vec3();
  readonly hydroForce = new Vec3();
  totalTime = 0;

  private readonly prevVel = new Vec3();
  /** Rotation matrix (row-major, local -> world), refreshed per substep. */
  private readonly mat = new Float64Array(9);
  /** Effective gravity in the ship frame (gravity minus hull acceleration). */
  readonly gLocal = new Vec3();
  private creakTimer = 0;
  /** World-space forces applied for the next step (grapples, contacts). */
  private readonly extraF = new Vec3();
  private readonly extraT = new Vec3();

  constructor(x: number, z: number, heading: number, plan = 3, cls: ShipClass = SHIP_CLASSES.frigate) {
    this.cls = cls;
    this.sx = cls.scale.x;
    this.sy = cls.scale.y;
    this.sz = cls.scale.z;
    this.colDx = cls.columns.dx;
    this.sails = new SailRig(plan, cls.sails, cls.plans, cls.masts);
    this.buildColumns(cls.columns.dx, cls.columns.dz);
    let vol = 0;
    const mom = new Vec3();
    for (const c of this.columns) {
      for (let j = 0; j < c.ys.length; j++) {
        const yTop = c.ys[j]! + c.dys[j]! / 2;
        const yBot = c.ys[j]! - c.dys[j]! / 2;
        const sub = clamp(-yBot, 0, yTop - yBot);
        const v = c.area * sub;
        vol += v;
        mom.x += c.x * v;
        mom.y += (yBot + sub / 2) * v;
      }
    }
    this.displacement = vol;
    this.mass = vol * RHO;
    // Centre of gravity sits over the centre of buoyancy (level trim).
    this.cg.set(mom.x / vol, cls.cgY * this.sy, 0);
    const m = this.mass;
    const B = 10.6 * this.sz, L = 36 * this.sx;
    // Radii of gyration typical of a sailing warship, plus hydrodynamic added inertia.
    this.inertia.set(m * Math.pow(0.4 * B, 2) * 1.25, m * Math.pow(0.27 * L, 2) * 1.4, m * Math.pow(0.26 * L, 2) * 1.8);
    this.buildCompartments();

    this.rot.setFromAxisAngle(0, 1, 0, -heading + Math.PI / 2);
    const cgw = this.cg.clone();
    this.rot.rotate(cgw);
    this.com.set(x + cgw.x, cgw.y, z + cgw.z);
    this.prevCom.copy(this.com);
    this.prevRot.copy(this.rot);
    this.updateTelemetry();
  }

  private buildColumns(dx: number, dz: number): void {
    const { sx, sy, sz } = this;
    for (let x = DECK_X_MIN + dx / 2; x < DECK_X_MAX; x += dx) {
      const top = deckY(x);
      const colsHere: Column[] = [];
      for (let z = dz / 2; z < 5.6; z += dz) {
        // Lowest y where the hull is at least |z| wide.
        const k = keelY(x) - 0.01;
        const s = sheerY(x);
        let lo = k, hi = Math.min(top, s);
        if (halfBreadthXY(x, hi) < z) continue;
        for (let it = 0; it < 30; it++) {
          const mid = (lo + hi) / 2;
          const hb = halfBreadthXY(x, mid);
          if (hb >= z) hi = mid;
          else lo = mid;
        }
        const bottom = hi;
        const height = top - bottom;
        if (height < 0.05) continue;
        const n = Math.max(1, Math.round((height * sy) / 0.55));
        const ys = new Float64Array(n);
        const dys = new Float64Array(n);
        for (let j = 0; j < n; j++) {
          dys[j] = (height / n) * sy;
          ys[j] = (bottom + (j + 0.5) * (height / n)) * sy;
        }
        for (const side of [1, -1]) {
          const col: Column = {
            x: x * sx, z: z * side * sz, ys, dys, area: dx * dz * sx * sz, latShare: 0,
            x0: 0, z0: 0, hasWarm: false, water: makeWaveSample(), lastSlam: -10, bow: x > 12,
          };
          colsHere.push(col);
          this.columns.push(col);
        }
      }
      for (const c of colsHere) c.latShare = 1 / Math.max(1, colsHere.length);
    }
    for (let x = -14; x <= 16; x += 2.5) {
      const y = deckY(x) + 0.15;
      const hw = deckHalfWidth(x);
      if (hw < 0) continue;
      for (const side of [1, -1]) {
        this.rail.push({ x: x * sx, y: y * sy, z: side * (hw + 0.1) * sz, side, last: -10, water: makeWaveSample(), x0: 0, z0: 0, warm: false });
      }
    }
  }

  /** Five compartments along the hold, their volumes from the columns. */
  private buildCompartments(): void {
    const bounds = [DECK_X_MIN, -9, -2.5, 4, 10.5, DECK_X_MAX];
    for (let i = 0; i < 5; i++) {
      const x0 = bounds[i]!, x1 = bounds[i + 1]!;
      let cap = 0, cx = 0, bottom = Infinity, top = -Infinity, hb = 0, nhb = 0;
      for (const c of this.columns) {
        const xd = c.x / this.sx;
        if (xd < x0 || xd >= x1) continue;
        for (let j = 0; j < c.ys.length; j++) {
          const v = c.area * c.dys[j]!;
          cap += v;
          cx += c.x * v;
          bottom = Math.min(bottom, c.ys[j]! - c.dys[j]! / 2);
          top = Math.max(top, c.ys[j]! + c.dys[j]! / 2);
        }
        hb += Math.abs(c.z);
        nhb++;
      }
      this.compartments.push({
        x0, x1, capacity: cap, water: 0, cx: cap > 0 ? cx / cap : ((x0 + x1) / 2) * this.sx,
        bottomY: Number.isFinite(bottom) ? bottom : -2, topY: Number.isFinite(top) ? top : 2,
        halfBeam: nhb > 0 ? (hb / nhb) * 1.4 : 3,
      });
    }
  }

  /** Compartment index for a design-space x. */
  compartmentAt(xd: number): number {
    for (let i = 0; i < this.compartments.length; i++) if (xd < this.compartments[i]!.x1) return i;
    return this.compartments.length - 1;
  }

  /** Total water aboard, kg. */
  get waterMass(): number {
    let w = 0;
    for (const c of this.compartments) w += c.water;
    return w * RHO;
  }

  /** Design-space point -> physical ship-local. */
  designToLocal(p: Vec3, out: Vec3): Vec3 {
    return out.set(p.x * this.sx, p.y * this.sy, p.z * this.sz);
  }

  /** Physical ship-local -> design space. */
  localToDesign(p: Vec3, out: Vec3): Vec3 {
    return out.set(p.x / this.sx, p.y / this.sy, p.z / this.sz);
  }

  /** Ship-local point -> world. */
  localToWorld(p: Vec3, out: Vec3): Vec3 {
    out.set(p.x - this.cg.x, p.y - this.cg.y, p.z - this.cg.z);
    this.rot.rotate(out);
    return out.add(this.com);
  }

  /** World point -> ship-local. */
  worldToLocal(p: Vec3, out: Vec3): Vec3 {
    out.copy(p).sub(this.com);
    this.rot.rotateInverse(out);
    return out.add(this.cg);
  }

  /** Design-space point -> world. */
  designToWorld(p: Vec3, out: Vec3): Vec3 {
    return this.localToWorld(this.designToLocal(p, out), out);
  }

  /** World-space velocity of a ship-local point. */
  pointVelocity(local: Vec3, out: Vec3): Vec3 {
    const r = tmpR.set(local.x - this.cg.x, local.y - this.cg.y, local.z - this.cg.z);
    this.rot.rotate(r);
    out.crossVectors(this.angVel, r);
    return out.add(this.vel);
  }

  /** World position of the ship-local origin (for rendering). */
  /** Move her bodily, keeping her attitude and way (the dev chart). */
  translate(dx: number, dy: number, dz: number): void {
    this.com.x += dx;
    this.com.y += dy;
    this.com.z += dz;
    // Render interpolation must not sweep her across the gap.
    this.prevCom.x += dx;
    this.prevCom.y += dy;
    this.prevCom.z += dz;
    // The sea under her is somewhere else now: sample it afresh.
    for (const c of this.columns) c.hasWarm = false;
    for (const r of this.rail) r.warm = false;
  }

  /** Turn her bodily about her centre of mass onto a new heading, keeping her heel, trim and way (the dev tools). */
  turnTo(heading: number): void {
    const q = tmpTurn.setFromAxisAngle(0, 1, 0, -wrapAngle(heading - this.heading));
    this.rot.copy(q.clone().multiply(this.rot)).normalize();
    this.prevRot.copy(this.rot);
    q.rotate(this.vel);
    q.rotate(this.angVel);
    for (const c of this.columns) c.hasWarm = false;
    for (const r of this.rail) r.warm = false;
    this.updateTelemetry();
  }

  origin(out: Vec3, alpha = 1): Vec3 {
    const q = tmpQ.copy(this.prevRot).slerp(this.rot, alpha);
    out.set(-this.cg.x, -this.cg.y, -this.cg.z);
    q.rotate(out);
    return out.add(tmpO.copy(this.prevCom).lerp(this.com, alpha));
  }

  /** Interpolated rotation for rendering. */
  renderRotation(out: Quat, alpha = 1): Quat {
    return out.copy(this.prevRot).slerp(this.rot, alpha);
  }

  /** Add a world-space force at a world point for the next step. */
  addForce(fx: number, fy: number, fz: number, px: number, py: number, pz: number): void {
    this.extraF.x += fx;
    this.extraF.y += fy;
    this.extraF.z += fz;
    const rx = px - this.com.x, ry = py - this.com.y, rz = pz - this.com.z;
    this.extraT.x += ry * fz - rz * fy;
    this.extraT.y += rz * fx - rx * fz;
    this.extraT.z += rx * fy - ry * fx;
  }

  /**
   * Water entering through a hole (design-space position, area m^2) during
   * dt, driven by the head of sea above it. Returns m^3 admitted.
   */
  leak(xd: number, yd: number, zd: number, area: number, dt: number): number {
    if (area <= 0) return 0;
    const p = this.designToWorld(tmpA.set(xd, yd, zd), tmpA);
    const c = this.nearestColumn(xd * this.sx, zd * this.sz);
    const head = (c ? c.water.height : 0) - p.y;
    if (head <= 0) return 0;
    const comp = this.compartments[this.compartmentAt(xd)]!;
    // Inflow slows as the water inside rises to the level outside.
    const fill = comp.water / Math.max(comp.capacity, 1e-3);
    const q = 0.62 * area * Math.sqrt(2 * G * head) * dt * Math.max(0, 1 - fill * fill);
    comp.water = Math.min(comp.capacity, comp.water + q);
    return q;
  }

  private nearestColumn(lx: number, lz: number): Column | null {
    let best: Column | null = null, bd = Infinity;
    for (const c of this.columns) {
      const d = (c.x - lx) * (c.x - lx) + (c.z - lz) * (c.z - lz);
      if (d < bd) { bd = d; best = c; }
    }
    return best;
  }

  step(dt: number, waves: WaveField, wind: Wind, controls: ShipControls, events: SimEvents, turbulence: number): void {
    if (this.sunk) {
      // Gone: settle toward the bottom.
      this.prevCom.copy(this.com);
      this.prevRot.copy(this.rot);
      this.com.y -= 1.2 * dt;
      return;
    }
    this.totalTime += dt;
    this.prevCom.copy(this.com);
    this.prevRot.copy(this.rot);
    this.prevVel.copy(this.vel);

    // Helm orders take effect as fast as the men at the wheel can turn it.
    this.helm = clamp(controls.helm, -1, 1);
    const rate = 0.3 * (0.08 + 0.92 * this.helmCrew);
    const targetRudder = this.rudderHealth > 0.05 ? this.helm * 0.62 : this.rudderJam;
    this.rudder += clamp(targetRudder - this.rudder, -rate * dt, rate * dt);

    // Sample the sea once per step for every column. The inversion of the
    // horizontal displacement is warm-started from last step's Lagrangian
    // coordinates, so a single evaluation per column suffices.
    const m = this.mat;
    quatToMat(this.rot, m);
    const cg = this.cg, com = this.com;
    for (const c of this.columns) {
      const lx = c.x - cg.x, ly = -cg.y, lz = c.z - cg.z;
      const wx = m[0]! * lx + m[1]! * ly + m[2]! * lz + com.x;
      const wz = m[6]! * lx + m[7]! * ly + m[8]! * lz + com.z;
      if (c.hasWarm) waves.sampleAt0(c.x0, c.z0, c.water, true);
      else waves.sample(wx, wz, c.water, true, 3);
      c.hasWarm = true;
      c.x0 = wx - c.water.dx;
      c.z0 = wz - c.water.dz;
    }

    // Sails see the apparent wind at their own (design-space) height.
    const sy = this.sy;
    const windLocal = (h: number, out: Vec3): Vec3 => {
      const worldH = this.com.y + h * sy - this.cg.y;
      const f = Wind.heightFactor(Math.max(0.5, worldH));
      out.set(wind.dirX * wind.speed * f - this.vel.x, 0, wind.dirZ * wind.speed * f - this.vel.z);
      this.rot.rotateInverse(out);
      return out;
    };
    const gLocal = this.gLocal.set(0, -G, 0);
    this.rot.rotateInverse(gLocal);
    gLocal.sub(this.accelLocal);

    this.updateFlooding(dt);

    // Substeps: the buoyancy spring is stiff, the rest is cheap. Coarse
    // (small, distant) hulls get one.
    const sub = this.colDx > 2 ? 1 : 2;
    const h = dt / sub;
    for (let i = 0; i < sub; i++) this.integrate(h, windLocal, i === 0 ? events : null);
    this.extraF.set(0, 0, 0);
    this.extraT.set(0, 0, 0);

    this.sails.update(dt, windLocal, gLocal, turbulence);

    // Hull acceleration in the ship frame (drives loose objects, cloth, camera).
    const a = tmpB.copy(this.vel).sub(this.prevVel).scale(1 / dt);
    this.rot.rotateInverse(a);
    this.accelLocal.lerp(a, 0.5);

    this.detectRail(waves, events);
    this.updateTelemetry();
    windLocal(24, this.apparentWind);

    const strain = Math.abs(this.angVelLocal.x) * 3 + Math.abs(this.angVelLocal.z) * 4 + Math.abs(this.heel) * 0.4;
    this.strain += (strain - this.strain) * Math.min(1, dt * 2);
    this.creakTimer -= dt;
    if (this.creakTimer <= 0 && this.strain > 0.05) {
      const lx = (-12 + ((this.totalTime * 7.31) % 1) * 26) * this.sx;
      const p = this.localToWorld(tmpA.set(lx, 1.5 * this.sy, 0), tmpA);
      events.push({ type: 'creak', intensity: Math.min(1, this.strain), x: p.x, y: p.y, z: p.z, pitch: ((this.totalTime * 3.7) % 1) });
      this.creakTimer = 0.35 + (1 - Math.min(1, this.strain)) * 2.2;
    }
  }

  /** Pumps, seepage between bulkheads, and water pouring over a submerged rail. */
  private updateFlooding(dt: number): void {
    const comps = this.compartments;
    // Bulkheads leak into their neighbours.
    for (let i = 0; i < comps.length - 1; i++) {
      const a = comps[i]!, b = comps[i + 1]!;
      const fa = a.water / Math.max(a.capacity, 1e-3), fb = b.water / Math.max(b.capacity, 1e-3);
      const flow = (fa - fb) * Math.min(a.capacity, b.capacity) * 0.02 * dt;
      a.water -= flow;
      b.water += flow;
    }
    // Pumps draw from the fullest compartment first.
    let pump = this.pumpRate * dt;
    const order = [...comps].sort((a, b) => b.water / b.capacity - a.water / a.capacity);
    for (const c of order) {
      const t = Math.min(pump, c.water);
      c.water -= t;
      pump -= t;
      if (pump <= 0) break;
    }
    // Reserve buoyancy: the hull volume above the waterline.
    let water = 0, cap = 0;
    for (const c of comps) {
      water += c.water;
      cap += c.capacity;
    }
    const reserve = Math.max(cap - this.displacement, 1);
    this.flooding = water / reserve;
  }

  private integrate(dt: number, windLocal: (h: number, out: Vec3) => Vec3, events: SimEvents | null): void {
    const m = this.mat;
    quatToMat(this.rot, m);
    const cg = this.cg, com = this.com, vel = this.vel, av = this.angVel;
    const waterKg = this.waterMass;
    let Fx = this.extraF.x, Fy = -this.mass * G + this.extraF.y, Fz = this.extraF.z;
    let Tx = this.extraT.x, Ty = this.extraT.y, Tz = this.extraT.z;
    let hx = 0, hy = 0, hz = 0;
    const upY = Math.max(0.25, m[4]!);
    let submergedVol = 0;
    let bowSpray = 0;
    const sx = this.sx, sy = this.sy, sz = this.sz;

    // Per column: integrate the submerged volume and its centroid along the
    // column, then apply buoyancy and hydrodynamic drag at that centroid.
    for (const c of this.columns) {
      const w = c.water;
      const lx = c.x - cg.x, lz = c.z - cg.z;
      const baseY = m[3]! * lx - m[4]! * cg.y + m[5]! * lz + com.y;
      const ys = c.ys, dys = c.dys;
      let vol = 0, ym = 0, latA = 0;
      for (let j = 0; j < ys.length; j++) {
        const dy = dys[j]!;
        const depth = w.height - (baseY + m[4]! * ys[j]!);
        const frac = clamp(depth / (dy * upY) + 0.5, 0, 1);
        if (frac <= 0) break;
        const dv = c.area * dy * frac;
        vol += dv;
        ym += ys[j]! * dv;
        latA += dy * frac;
      }
      if (vol <= 0) continue;
      submergedVol += vol;
      const ly = ym / vol - cg.y;
      const rx = m[0]! * lx + m[1]! * ly + m[2]! * lz;
      const ry = m[3]! * lx + m[4]! * ly + m[5]! * lz;
      const rz = m[6]! * lx + m[7]! * ly + m[8]! * lz;
      // Drag relative to the orbital motion of the water, which decays with depth.
      const decay = Math.exp(-Math.max(0, w.height - (com.y + ry)) * 0.18);
      const vx = vel.x + av.y * rz - av.z * ry - w.vx * decay;
      const vy = vel.y + av.z * rx - av.x * rz - w.vy * decay;
      const vz = vel.z + av.x * ry - av.y * rx - w.vz * decay;
      const ul = m[0]! * vx + m[3]! * vy + m[6]! * vz;
      const vl = m[1]! * vx + m[4]! * vy + m[7]! * vz;
      const wl = m[2]! * vx + m[5]! * vy + m[8]! * vz;
      // Keel/hull as a low-aspect-ratio foil: lift ~ u*w, plus cross-flow drag.
      const lat = 0.5 * RHO * latA * this.colDx * sx * c.latShare;
      const fzl = -lat * (1.1 * Math.abs(wl) * wl + 0.45 * Math.abs(ul) * wl) - 80 * vol * wl;
      const vert = 0.5 * RHO * c.area * Math.min(1, vol / (c.area * 0.6));
      const fyl = -vert * 1.1 * Math.abs(vl) * vl - 600 * vol * vl;
      const fx = m[1]! * fyl + m[2]! * fzl;
      const fy = m[4]! * fyl + m[5]! * fzl;
      const fz = m[7]! * fyl + m[8]! * fzl;
      hx += fx;
      hy += fy;
      hz += fz;
      const fyt = fy + RHO * G * vol;
      Fx += fx;
      Fy += fyt;
      Fz += fz;
      Tx += ry * fz - rz * fyt;
      Ty += rz * fx - rx * fz;
      Tz += rx * fyt - ry * fx;
      if (c.bow && events) {
        bowSpray = Math.max(bowSpray, -vl);
        // Bow slamming into a sea.
        if (Math.abs(c.z) < 2.2 * sz && Math.abs(w.height - baseY) < 1.2 && vy < -2.2 && this.totalTime - c.lastSlam > 0.9) {
          c.lastSlam = this.totalTime;
          events.push({ type: 'slam', x: com.x + rx, y: w.height, z: com.z + rz, intensity: clamp((-vy - 2.2) / 3, 0.1, 1), side: Math.sign(c.z) });
        }
      }
    }
    this.hydroForce.set(hx, hy, hz);
    const F = tmpF.set(Fx, Fy, Fz);
    const T = tmpT.set(Tx, Ty, Tz);
    this.immersion = submergedVol / this.displacement;
    this.bowSpray = clamp(Math.max(this.speed / 6, 0) * 0.5 + bowSpray / 4, 0, 1);

    // Water in the hold: its weight, sloshing to the low side (free surface).
    const right = tmpE.set(0, 0, 1);
    this.rot.rotate(right);
    for (const c of this.compartments) {
      if (c.water <= 1e-3) continue;
      const fill = clamp(c.water / Math.max(c.capacity, 1e-3), 0, 1);
      const y = c.bottomY + (c.topY - c.bottomY) * fill * 0.5;
      // The surface stays level, so the water piles up on the low side.
      const shift = clamp(-right.y * 2.2, -1, 1) * c.halfBeam * (1 - fill) * 0.8;
      const wf = tmpH.set(0, -c.water * RHO * G, 0);
      const r = tmpI.set(c.cx - cg.x, y - cg.y, shift - cg.z);
      this.rot.rotate(r);
      F.add(wf);
      T.add(tmpJ.crossVectors(r, wf));
    }

    // Longitudinal resistance (friction + wave-making, steep near hull speed).
    const vl = tmpE.copy(this.vel);
    this.rot.rotateInverse(vl);
    const vHull = 7.2 * Math.sqrt(sx);
    const imm = clamp(this.immersion, 0, 1.8);
    const resist = -Math.sign(vl.x) * 1100 * sx * sz * vl.x * vl.x * (1 + 3 * Math.pow(vl.x / vHull, 4)) * imm * this.dragFactor;
    this.applyLocalForce(resist, 0, 0, 0.5 * sx, -1.2 * sy, 0, F, T);

    // Rudder: a foil in the flow past the stern post.
    const rp = tmpB.set(-17.3 * sx, -2.3 * sy, 0);
    const rv = this.pointVelocity(rp, tmpC);
    this.rot.rotateInverse(rv);
    const flowU = rv.x;
    const drift = Math.atan2(rv.z, Math.max(Math.abs(flowU), 0.4));
    const alpha = clamp(this.rudder * Math.sign(flowU || 1) + drift * 0.6, -0.9, 0.9);
    const eff = 0.15 + 0.85 * this.rudderHealth;
    const qr = 0.5 * RHO * (flowU * flowU + rv.z * rv.z) * 6.5 * sx * sy * eff;
    const cl = Math.abs(alpha) < 0.6 ? 2.8 * alpha : Math.sign(alpha) * 2.8 * 0.6 * (1 - (Math.abs(alpha) - 0.6));
    const cd = 0.03 + 1.2 * alpha * alpha;
    this.applyLocalForce(-Math.sign(flowU) * qr * cd, 0, -qr * cl * Math.sign(flowU || 1), rp.x, rp.y, rp.z, F, T);

    // Sails (their forces scale with the rig's area).
    const sf = this.sailForce.set(0, 0, 0);
    const ka = sx * sy * this.cls.sailArea;
    for (const s of this.sails.sails) {
      if (s.deploy < 0.02 || s.lost) continue;
      const lf = s.force;
      sf.x += lf.x * ka;
      sf.z += lf.z * ka;
      // Centre of effort drops as sails are reefed.
      this.applyLocalForce(lf.x * ka, 0, lf.z * ka, s.ce.x * sx, (s.ce.y - (1 - s.deploy) * 1.5) * sy, s.ce.z * sz, F, T);
    }

    // Windage of hull, spars and rigging.
    const aw = windLocal(10, tmpB);
    const qa = 0.5 * AIR;
    this.applyLocalForce(qa * 110 * 0.8 * sx * sy * Math.abs(aw.x) * aw.x, 0, qa * 260 * 0.9 * sx * sy * Math.abs(aw.z) * aw.z, 0, 9 * sy, 0, F, T);

    // Integrate (semi-implicit Euler, per-axis effective mass for added mass).
    const mEff = this.mass + waterKg;
    const Fl = tmpE.copy(F);
    this.rot.rotateInverse(Fl);
    Fl.x /= mEff * 1.05;
    Fl.y /= mEff * 1.9;
    Fl.z /= mEff * 1.6;
    this.rot.rotate(Fl);
    this.vel.addScaled(Fl, dt);
    this.com.addScaled(this.vel, dt);

    const Tl = tmpF.copy(T);
    this.rot.rotateInverse(Tl);
    const w = tmpG.copy(this.angVel);
    this.rot.rotateInverse(w);
    const I = this.inertia;
    const k = 1 + waterKg / this.mass;
    // Euler's equations (gyroscopic term) with a little structural damping.
    const gx = (I.z - I.y) * w.y * w.z;
    const gy = (I.x - I.z) * w.z * w.x;
    const gz = (I.y - I.x) * w.x * w.y;
    w.x += ((Tl.x - gx) / (I.x * k)) * dt;
    w.y += ((Tl.y - gy) / (I.y * k)) * dt;
    w.z += ((Tl.z - gz) / (I.z * k)) * dt;
    w.x *= 1 - 0.05 * dt;
    w.y *= 1 - 0.1 * dt;
    w.z *= 1 - 0.1 * dt;
    this.rot.rotate(w);
    this.angVel.copy(w);
    this.rot.integrate(w.x, w.y, w.z, dt);
  }

  /** Apply a ship-local force at a ship-local point. */
  private applyLocalForce(fx: number, fy: number, fz: number, px: number, py: number, pz: number, F: Vec3, T: Vec3): void {
    const f = tmpH.set(fx, fy, fz);
    this.rot.rotate(f);
    const r = tmpI.set(px - this.cg.x, py - this.cg.y, pz - this.cg.z);
    this.rot.rotate(r);
    F.add(f);
    T.add(tmpJ.crossVectors(r, f));
  }

  /** Apply an instantaneous world impulse at a world point (e.g. gun recoil). */
  applyImpulse(ix: number, iy: number, iz: number, px: number, py: number, pz: number): void {
    this.vel.x += ix / this.mass;
    this.vel.y += iy / this.mass;
    this.vel.z += iz / this.mass;
    const r = tmpI.set(px - this.com.x, py - this.com.y, pz - this.com.z);
    const L = tmpJ.crossVectors(r, tmpH.set(ix, iy, iz));
    this.rot.rotateInverse(L);
    L.x /= this.inertia.x;
    L.y /= this.inertia.y;
    L.z /= this.inertia.z;
    this.rot.rotate(L);
    this.angVel.add(L);
  }

  /** Green water: seas higher than the rail pour onto the deck (and below). */
  private detectRail(waves: WaveField, events: SimEvents): void {
    let worst = 0;
    let under = 0;
    for (const rp of this.rail) {
      const p = this.localToWorld(tmpA.set(rp.x, rp.y, rp.z), tmpA);
      // Warm-started from last step's answer, as the columns are: one evaluation.
      const s = rp.warm ? waves.sampleAt0(rp.x0, rp.z0, rp.water, true) : waves.sample(p.x, p.z, rp.water, true, 2);
      rp.warm = true;
      rp.x0 = p.x - s.dx;
      rp.z0 = p.z - s.dz;
      const over = s.height - p.y;
      if (over > 0) {
        under++;
        worst = Math.max(worst, over);
        this.deckWaterSide = rp.side;
        // A deck awash floods the hold through hatches and shot holes.
        const comp = this.compartments[this.compartmentAt(rp.x / this.sx)]!;
        comp.water = Math.min(comp.capacity, comp.water + over * 0.35 * this.sx * (1 / 60));
        if (this.totalTime - rp.last > 1.2) {
          rp.last = this.totalTime;
          events.push({ type: 'washOver', x: p.x, y: p.y, z: p.z, intensity: clamp(over / 0.8, 0.1, 1), side: rp.side, localX: rp.x });
        }
      }
    }
    this.deckWater = Math.max(this.deckWater * Math.exp(-0.25 * (1 / 60)), clamp(worst / 0.6, 0, 1));
    // Gone: the whole rail under for a while and still sinking.
    if (under >= this.rail.length * 0.9) this.underTime += 1 / 60;
    else this.underTime = Math.max(0, this.underTime - 1 / 30);
    if (this.underTime > 12 && this.com.y < -6 * this.sy) this.sunk = true;
  }

  private updateTelemetry(): void {
    const fwd = tmpA.set(1, 0, 0);
    this.rot.rotate(fwd);
    this.heading = vecToBearing(fwd.x, fwd.z);
    this.pitch = Math.asin(clamp(fwd.y, -1, 1));
    const right = tmpB.set(0, 0, 1);
    this.rot.rotate(right);
    // Positive heel = heeled to starboard (starboard rail down).
    this.heel = Math.asin(clamp(-right.y, -1, 1));
    const vl = tmpC.copy(this.vel);
    this.rot.rotateInverse(vl);
    this.speed = vl.x;
    this.sog = Math.hypot(this.vel.x, this.vel.z);
    this.leeway = Math.abs(vl.x) > 0.3 ? Math.atan2(vl.z, vl.x) : 0;
    this.angVelLocal.copy(this.angVel);
    this.rot.rotateInverse(this.angVelLocal);
  }
}

const tmpA = new Vec3(), tmpB = new Vec3(), tmpC = new Vec3(), tmpE = new Vec3();
const tmpF = new Vec3(), tmpT = new Vec3(), tmpG = new Vec3(), tmpH = new Vec3();
const tmpI = new Vec3(), tmpJ = new Vec3(), tmpR = new Vec3();
const tmpQ = new Quat(), tmpTurn = new Quat();
const tmpO = new Vec3();

/** Quaternion to a row-major 3x3 rotation matrix. */
function quatToMat(q: Quat, m: Float64Array): void {
  const { x, y, z, w } = q;
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2;
  const yy = y * y2, yz = y * z2, zz = z * z2;
  const wx = w * x2, wy = w * y2, wz = w * z2;
  m[0] = 1 - (yy + zz); m[1] = xy - wz; m[2] = xz + wy;
  m[3] = xy + wz; m[4] = 1 - (xx + zz); m[5] = yz - wx;
  m[6] = xz - wy; m[7] = yz + wx; m[8] = 1 - (xx + yy);
}
